import type { Melody, MelodyChordLane, MelodyEntityNote, NoteName, Scale } from '@tunasong/schemas'
import { noteNameSchema } from '@tunasong/schemas'
import { chordTones, type ChordTone } from '../chord/chord-tones.js'
import { getScaleSpec } from '../scale/scale.js'
import { chordAt } from './chord-lane.js'
import { bassPitchClass } from './chord-voicing.js'
import { barBeats, feltBeat, GRID_TOLERANCE } from './grid.js'

/**
 * What each note is doing over the chord sounding under it (melody toolkit plan M3; spec §5.3). Bowie's
 * rules and his fixtures (plan appendix §2); pure, so every rule is tested without a screen.
 *
 * A note is judged against the chord at its onset, and again at each change it is held across. A note
 * that starts up to half a beat before a change and is held across it belongs to the next chord only (an
 * anticipation). The first role that fits, in Bowie's order:
 *
 * 1. Chord tone. 2. Blue note. 3. Accented and resolving: a suspension if prepared, else it leans on the
 * beat. 4. Accented, a semitone above a chord tone, not resolving: an avoid note (over a dominant 7th the
 * ♭9 and ♭13 are tensions instead). 5. Unaccented and stepwise: passing or neighbor. 6. Tension. 7. Outside.
 *
 * With no chords at all and a key, each note is a scale degree. With neither, there is nothing to say.
 */

export const noteRoleKinds = [
  'chord-tone',
  'blue',
  'suspension',
  'leans',
  'avoid',
  'passing',
  'neighbor',
  'tension',
  'outside',
  'scale-degree',
] as const

export type NoteRoleKind = (typeof noteRoleKinds)[number]

export interface MelodyNoteRole {
  noteIndex: number
  /** The chord the note is judged against, as the lane names it; null for a scale degree */
  chord: string | null
  /** Where this judgement starts: the note's onset, or the change it is held across */
  from: number
  role: NoteRoleKind
  /** "root", "3rd", "♭9", "4th", "major 7th"; a scale degree "♭3", or "1" for the tonic */
  degree: string
  /**
   * D8 (Bowie): the chord-relative degree number Advanced draws on the note, "♭7", "9", "♯11", "𝄫7"; the
   * interval above the root, where the why line names the role in the chord ("the 7th")
   */
  degreeNumber: string
  /** The note spelled: a chord tone as the chord spells it, any other note by where it goes */
  name: NoteName
  /** Where a suspension, a lean or an avoid note goes */
  target?: { name: NoteName; direction: 'falls' | 'rises' }
  /** Why an accented note is accented: it starts on a strong beat, or it is held longer than a beat */
  accent?: 'strong' | 'held'
  /** Passing and neighbor notes: the notes either side, spelled */
  neighbors?: { before?: NoteName; after?: NoteName }
  /** A scale degree in the key's own scale: its step, 1–7 */
  step?: number
  /** A scale degree outside the key's scale: the two steps it sits between */
  between?: [number, number]
}

const mod12 = (value: number) => ((value % 12) + 12) % 12

const letters = ['C', 'D', 'E', 'F', 'G', 'A', 'B']

/** With neither key nor chord, the chromatic notes as Bowie fixed them: C♯ E♭ F♯ A♭ B♭ */
const fixedNames: NoteName[] = ['C', 'C#', 'D', 'Eb', 'E', 'F', 'F#', 'G', 'Ab', 'A', 'Bb', 'B']

const sharpNames: NoteName[] = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B']
const flatNames: NoteName[] = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'Gb', 'G', 'Ab', 'A', 'Bb', 'B']

const accidentalNames: Record<number, string> = { [-2]: 'bb', [-1]: 'b', 0: '', 1: '#', 2: '##' }

/** `letter` (0 C … 6 B) bent to sound `pitch`: "Gb", "F#", "E#"; a flat or sharp name when no such name exists */
const nameOf = (letter: number, pitch: number, isRising: boolean): NoteName => {
  const natural = [0, 2, 4, 5, 7, 9, 11][letter] ?? 0
  const bend = mod12(pitch - natural + 6) - 6
  const parsed = noteNameSchema.safeParse(`${letters[letter] ?? 'C'}${accidentalNames[bend] ?? 'x'}`)
  return parsed.success ? parsed.data : ((isRising ? sharpNames : flatNames)[mod12(pitch)] ?? 'C')
}

/** The letter a name is spelled on, 0 C … 6 B */
const letterOf = (name: string) => Math.max(0, letters.indexOf(name[0] ?? 'C'))

const romanIndex = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII'] as const
const majorSemitones = [0, 2, 4, 5, 7, 9, 11]

/** The key's degrees as [letter offset from the tonic's letter, semitones above the tonic] */
const keyDegrees = (key: Scale) =>
  getScaleSpec(key).intervals.flatMap(interval => {
    const match = /^(b|#)?(VII|VI|IV|V|III|II|I)$/.exec(interval)
    const index = match ? romanIndex.indexOf(match[2] as (typeof romanIndex)[number]) : -1
    if (!match || index < 0) {
      return []
    }
    const accidental = match[1] === 'b' ? -1 : match[1] === '#' ? 1 : 0
    return [{ letter: index, semitones: mod12((majorSemitones[index] ?? 0) + accidental) }]
  })

interface ChordInfo {
  symbol: string
  tones: ChordTone[]
  rootPitch: number
  /** The root as the chord spells it, the letter its degrees are counted from */
  rootName: string
  hasThird: boolean
  hasMinorThird: boolean
  hasSeventh: boolean
  isDominant: boolean
}

const chordInfo = (symbol: string): ChordInfo | null => {
  const tones = chordTones(symbol)
  const root = tones?.find(tone => tone.degree === 1)
  if (!tones || !root) {
    return null
  }
  const third = tones.find(tone => tone.degree === 3)
  const seventh = tones.find(tone => tone.degree === 7)
  const thirdInterval = third ? mod12(third.pitchClass - root.pitchClass) : undefined
  return {
    symbol,
    tones,
    rootPitch: root.pitchClass,
    rootName: root.name,
    hasThird: third !== undefined,
    hasMinorThird: thirdInterval === 3,
    hasSeventh: seventh !== undefined,
    isDominant: thirdInterval === 4 && seventh !== undefined && mod12(seventh.pitchClass - root.pitchClass) === 10,
  }
}

/** A chord tone's name by its function: root, 3rd, 5th, 6th, 7th, 9th, 11th, 13th, keeping alterations */
const chordToneDegree = (tone: ChordTone, chord: ChordInfo) => {
  const interval = mod12(tone.pitchClass - chord.rootPitch)
  switch (tone.degree) {
    case 1:
      return 'root'
    case 2:
      if (!chord.hasThird) {
        return '2nd'
      }
      return interval === 1 ? '♭9' : interval === 3 ? '♯9' : '9th'
    case 3:
      return '3rd'
    case 4:
      if (!chord.hasThird) {
        return '4th'
      }
      return interval === 6 ? '♯11' : '11th'
    case 5:
      return '5th'
    case 6:
      return chord.hasSeventh ? (interval === 8 ? '♭13' : '13th') : '6th'
    default:
      return '7th'
  }
}

/** (c) the default for a pitch outside the key: ♭2 ♭3 ♯4 ♭6 ♭7, as letter steps above the tonic or root */
const defaultSteps = [0, 1, 1, 2, 2, 3, 3, 4, 5, 5, 6, 6]

const triadNames: Record<number, string> = {
  0: 'root',
  1: '♭2',
  2: '2nd',
  3: '♭3',
  4: '3rd',
  5: '4th',
  6: '♯4',
  7: '5th',
  8: '♭6',
  9: '6th',
  10: '♭7',
  11: 'major 7th',
}

const extendedNames: Record<number, string> = {
  ...triadNames,
  1: '♭9',
  2: '9th',
  3: '♯9',
  5: '11th',
  6: '♯11',
  8: '♭13',
  9: '13th',
}

/**
 * Bowie's ruling (2026-10-23, narrowed): the chord sets the name, one per semitone above the root, and over a
 * triad only the spelling picks between two names, in three places. Keyed by semitones, then by letters from
 * the root to the spelled note: F♯ over C is the ♯4, G♭ the ♭5. Over a 7th chord the names stay fixed.
 */
const spelledPairs: Record<number, Record<number, string>> = {
  3: { 1: '♯2', 2: '♭3' },
  6: { 3: '♯4', 4: '♭5' },
  8: { 4: '♯5', 5: '♭6' },
}

const pairName = (name: string, rootName: string, interval: number) =>
  spelledPairs[interval]?.[(((letterOf(name) - letterOf(rootName)) % 7) + 7) % 7]

/** A note that is not a chord tone, named for the chord: a triad's small numbers, a 7th chord's extensions */
const intervalDegree = (name: string, interval: number, chord: ChordInfo) =>
  chord.hasSeventh
    ? (extendedNames[interval] ?? '')
    : (pairName(name, chord.rootName, interval) ?? triadNames[interval] ?? '')

/** A suspension is always named by the small number (Bowie), a rising 7–8 included */
const suspensionDegree = (interval: number) => triadNames[interval] ?? ''

/** D8: a chord tone's number, from its degree and its interval above the root */
const chordToneNumber = (tone: ChordTone, chord: ChordInfo) => {
  const interval = mod12(tone.pitchClass - chord.rootPitch)
  switch (tone.degree) {
    case 1:
      return '1'
    case 2:
      return chord.hasThird ? ({ 1: '♭9', 3: '♯9' }[interval] ?? '9') : interval === 1 ? '♭2' : '2'
    case 3:
      return interval === 3 ? '♭3' : '3'
    case 4:
      return chord.hasThird ? (interval === 6 ? '♯11' : '11') : '4'
    case 5:
      return { 6: '♭5', 8: '♯5' }[interval] ?? '5'
    case 6:
      return chord.hasSeventh ? (interval === 8 ? '♭13' : '13') : '6'
    default:
      return { 9: '𝄫7', 10: '♭7' }[interval] ?? '7'
  }
}

/** D8: a note that is not a chord tone, numbered for the chord as the why line names it */
const triadNumbers: Record<number, string> = {
  0: '1',
  1: '♭2',
  2: '2',
  3: '♭3',
  4: '3',
  5: '4',
  6: '♯4',
  7: '5',
  8: '♭6',
  9: '6',
  10: '♭7',
  11: '7',
}

const extendedNumbers: Record<number, string> = {
  ...triadNumbers,
  1: '♭9',
  2: '9',
  3: '♯9',
  5: '11',
  6: '♯11',
  8: '♭13',
  9: '13',
}

const intervalNumber = (name: string, interval: number, chord: ChordInfo) =>
  chord.hasSeventh
    ? (extendedNumbers[interval] ?? '')
    : (pairName(name, chord.rootName, interval) ?? triadNumbers[interval] ?? '')

/** A scale degree's name in the key, with no chords; ♯4 or ♭5 as the spelling picks */
const scaleDegreeNames: Record<number, string> = {
  0: '1',
  1: '♭2',
  2: '2',
  3: '♭3',
  4: '3',
  5: '4',
  7: '5',
  8: '♭6',
  9: '6',
  10: '♭7',
  11: '7',
}

const isStep = (a: number, b: number) => {
  const distance = Math.abs(a - b)
  return distance === 1 || distance === 2
}

interface Span {
  chord: ChordInfo
  from: number
  to: number
  isHeldIn: boolean
}

/** The chord spans a note is judged in, an anticipation folded into the chord it anticipates */
const spansOf = (note: MelodyEntityNote, lane: MelodyChordLane, meter: Melody['tempo']['meter']): Span[] => {
  const end = note.onset + note.duration
  const spans = lane.chords.flatMap(laneChord => {
    const from = Math.max(note.onset, laneChord.onset)
    const to = Math.min(end, laneChord.onset + laneChord.duration)
    if (to - from < GRID_TOLERANCE) {
      return []
    }
    const chord = chordInfo(laneChord.symbol)
    return chord ? [{ chord, from, to, isHeldIn: note.onset < laneChord.onset - GRID_TOLERANCE }] : []
  })
  const [first, second] = spans
  const isAnticipation =
    first !== undefined &&
    second !== undefined &&
    second.isHeldIn &&
    first.to - first.from <= feltBeat(meter) / 2 + GRID_TOLERANCE
  return isAnticipation ? spans.slice(1) : spans
}

/** The key's pitches, and the note set a chord makes of them */
const keyContext = (key: Scale | undefined) => {
  const keyRoot = key ? bassPitchClass(key.root) : undefined
  const degrees = key ? keyDegrees(key) : []
  const keyPitches = keyRoot === undefined ? [] : degrees.map(degree => mod12(keyRoot + degree.semitones))
  const isMinorKey = degrees.some(degree => degree.letter === 2 && degree.semitones === 3)

  /** The key's scale, each chord tone replacing its letter neighbour: B♭ replaces B under a B♭ chord in C */
  const noteSetOf = (chord: ChordInfo) => {
    if (keyRoot === undefined || degrees.length !== 7 || !key) {
      return new Set([...keyPitches, ...chord.tones.map(tone => tone.pitchClass)])
    }
    const tonicLetter = letters.indexOf(key.root[0] ?? 'C')
    return new Set(
      degrees.map((degree, index) => {
        const letter = letters[(tonicLetter + degree.letter) % 7]
        const tone = chord.tones.find(entry => entry.name[0] === letter)
        return tone ? tone.pitchClass : (keyPitches[index] ?? 0)
      })
    )
  }
  const tonicLetter = key ? letterOf(key.root) : 0
  const isInKey = (pitch: number) => keyPitches.includes(pitch)

  /**
   * Bowie's ruling (2026-10-23), for a note that is not a chord tone; the first rule that applies. A note of
   * the key keeps the key's spelling. (a) A semitone from the next note, spell toward it: sharp rising, flat
   * falling (#2418). (b) Chromatic notes in a row moving one way share sharps rising, flats falling. (c) The
   * key's default. With no key: (a), then the chord root's major table, or with no chord either a fixed set,
   * C♯ E♭ F♯ A♭ B♭ (Bowie, 2026-10-23).
   */
  const spell = (notes: MelodyEntityNote[], index: number, chord?: ChordInfo): NoteName => {
    const note = notes[index]
    if (!note) {
      return 'C'
    }
    const pitch = mod12(note.midi)
    const next = notes[index + 1]
    const previous = notes[index - 1]
    if (keyRoot !== undefined && isInKey(pitch)) {
      const degreeIndex = keyPitches.indexOf(pitch)
      return nameOf((tonicLetter + (degrees[degreeIndex]?.letter ?? 0)) % 7, pitch, true)
    }
    if (next && Math.abs(next.midi - note.midi) === 1) {
      return (next.midi > note.midi ? sharpNames : flatNames)[pitch] ?? 'C'
    }
    if (keyRoot === undefined) {
      return chord
        ? nameOf((letterOf(chord.rootName) + (defaultSteps[mod12(pitch - chord.rootPitch)] ?? 0)) % 7, pitch, true)
        : (fixedNames[pitch] ?? 'C')
    }
    const neighbour =
      previous && !isInKey(mod12(previous.midi)) && previous.midi !== note.midi
        ? Math.sign(note.midi - previous.midi)
        : next && !isInKey(mod12(next.midi)) && next.midi !== note.midi
          ? Math.sign(next.midi - note.midi)
          : 0
    if (neighbour !== 0) {
      return (neighbour > 0 ? sharpNames : flatNames)[pitch] ?? 'C'
    }
    const steps = defaultSteps[mod12(pitch - keyRoot)] ?? 0
    return nameOf((tonicLetter + steps) % 7, pitch, steps === 3)
  }

  return { keyRoot, degrees, isMinorKey, noteSetOf, spell }
}

export const noteRoles = (melody: Melody, lane: MelodyChordLane, key: Scale | undefined): MelodyNoteRole[] => {
  const { meter } = melody.tempo
  const bar = barBeats(meter)
  const beat = feltBeat(meter)
  /** Beat 1, and the bar's midpoint in even meters (4/4 beat 3, the 4th eighth of 6/8, the 7th of 12/8) */
  const isStrong = (at: number) => {
    const inBar = ((at % bar) + bar) % bar
    return inBar < GRID_TOLERANCE || (meter.top % 2 === 0 && Math.abs(inBar - bar / 2) < GRID_TOLERANCE)
  }

  const { keyRoot, degrees, isMinorKey, noteSetOf, spell } = keyContext(key)

  const isBlue = (pitch: number, chord: ChordInfo) => {
    if (keyRoot === undefined) {
      return !chord.hasMinorThird && mod12(pitch - chord.rootPitch) === 3
    }
    if (isMinorKey) {
      return mod12(pitch - keyRoot) === 6
    }
    const fromKey = mod12(pitch - keyRoot)
    return !chord.hasMinorThird && (fromKey === 3 || fromKey === 10)
  }

  const { notes } = melody
  if (lane.chords.length === 0) {
    if (keyRoot === undefined) {
      return []
    }
    return notes.map((note, noteIndex) => {
      const interval = mod12(note.midi - keyRoot)
      const name = spell(notes, noteIndex)
      const degree = pairName(name, key?.root ?? 'C', interval) ?? scaleDegreeNames[interval] ?? ''
      const stepIndex = degrees.findIndex(entry => entry.semitones === interval)
      const below = degrees.reduce((last, entry, index) => (entry.semitones < interval ? index : last), 0)
      const place =
        stepIndex >= 0
          ? { step: stepIndex + 1 }
          : { between: [below + 1, ((below + 1) % degrees.length) + 1] as [number, number] }
      return {
        noteIndex,
        chord: null,
        from: note.onset,
        role: 'scale-degree' as const,
        degree,
        degreeNumber: degree,
        name,
        ...place,
      }
    })
  }

  return notes.flatMap((note, noteIndex) => {
    const previous = notes[noteIndex - 1]
    const next = notes[noteIndex + 1]
    const pitch = mod12(note.midi)
    return spansOf(note, lane, meter).map(({ chord, from, to, isHeldIn }): MelodyNoteRole => {
      const interval = mod12(pitch - chord.rootPitch)
      const ownTone = chord.tones.find(tone => tone.pitchClass === pitch)
      /** ONE decision, the spelling: (0) a chord tone as the chord spells it, any other note by `spell` */
      const name = ownTone ? ownTone.name : spell(notes, noteIndex, chord)
      const degreeNumber = ownTone ? chordToneNumber(ownTone, chord) : intervalNumber(name, interval, chord)
      const base = { noteIndex, chord: chord.symbol, from, degreeNumber }
      if (ownTone) {
        return { ...base, role: 'chord-tone', degree: chordToneDegree(ownTone, chord), name }
      }

      /**
       * Accented: on a strong beat, or held longer than a felt beat. On a strong beat it is the held form
       * once it lasts longer than half a bar (Bowie's F1: B♭ held through a bar of F).
       */
      const length = to - from
      const isLong = length > beat + GRID_TOLERANCE
      const accent = isStrong(from)
        ? length > bar / 2 + GRID_TOLERANCE
          ? ('held' as const)
          : ('strong' as const)
        : isLong
          ? ('held' as const)
          : undefined
      const below = chord.tones.find(tone => tone.pitchClass === mod12(pitch - 1))
      const isDominantException = chord.isDominant && (interval === 1 || interval === 8)
      /**
       * Bowie's ruling (2026-10-23): an accented note a semitone above a chord tone is a suspension, a lean or
       * an avoid note, never blue: B♭ over F in C is the 4th leaning on A. The dominant exception keeps blue.
       */
      const isAccentedAboveChordTone = accent !== undefined && below !== undefined && !isDominantException
      if (!isAccentedAboveChordTone && isBlue(pitch, chord)) {
        return { ...base, role: 'blue', degree: intervalDegree(name, interval, chord), name }
      }

      const nextChord = next ? chordAt(lane, next.onset) : null
      const nextTone =
        next && isStep(next.midi, note.midi) && nextChord
          ? chordInfo(nextChord.symbol)?.tones.find(tone => tone.pitchClass === mod12(next.midi))
          : undefined
      if (accent && next && nextTone) {
        const isPrepared = isHeldIn || previous?.midi === note.midi
        return {
          ...base,
          role: isPrepared ? 'suspension' : 'leans',
          degree: isPrepared ? suspensionDegree(interval) : intervalDegree(name, interval, chord),
          name,
          target: { name: nextTone.name, direction: next.midi > note.midi ? 'rises' : 'falls' },
          accent,
        }
      }

      if (accent && below && !isDominantException) {
        return {
          ...base,
          role: 'avoid',
          degree: intervalDegree(name, interval, chord),
          name,
          target: { name: below.name, direction: 'falls' },
          accent,
        }
      }

      const stepsIn = previous !== undefined && isStep(previous.midi, note.midi)
      const stepsOut = next !== undefined && isStep(next.midi, note.midi)
      if (!accent && (stepsIn || stepsOut)) {
        const isPassing =
          stepsIn &&
          stepsOut &&
          previous !== undefined &&
          next !== undefined &&
          Math.sign(note.midi - previous.midi) === Math.sign(next.midi - note.midi)
        return {
          ...base,
          role: isPassing ? 'passing' : 'neighbor',
          degree: intervalDegree(name, interval, chord),
          name,
          neighbors: {
            ...(stepsIn && previous ? { before: spell(notes, noteIndex - 1, chord) } : {}),
            ...(stepsOut && next ? { after: spell(notes, noteIndex + 1, chord) } : {}),
          },
        }
      }

      const isMajorChord = chord.hasThird && !chord.hasMinorThird
      const isTension =
        noteSetOf(chord).has(pitch) ||
        (chord.isDominant && [1, 3, 6, 8].includes(interval)) ||
        (isMajorChord && interval === 6)
      return { ...base, role: isTension ? 'tension' : 'outside', degree: intervalDegree(name, interval, chord), name }
    })
  })
}

export type RowFit = 'chord' | 'tension' | 'avoid'

/**
 * Spec §5.3 "What fits here": while a note is drawn or dragged, the rows shade for the chord under the
 * cursor, by pitch class. Chord tones strongest; tensions lighter (the note set, and over a dominant the
 * altered 9ths, ♯11 and ♭13, and ♯11 over a major chord); avoid notes, a semitone above a chord tone,
 * hatched. It follows the chord's own notes, so the B♭ row lights up over a B♭ chord in C.
 */
export const fitsAt = (lane: MelodyChordLane, key: Scale | undefined, beat: number): Map<number, RowFit> => {
  const laneChord = chordAt(lane, beat)
  const chord = laneChord ? chordInfo(laneChord.symbol) : null
  const fits = new Map<number, RowFit>()
  if (!chord) {
    return fits
  }
  const chordPitches = new Set(chord.tones.map(tone => tone.pitchClass))
  const isMajorChord = chord.hasThird && !chord.hasMinorThird
  const noteSet = keyContext(key).noteSetOf(chord)
  for (let pitch = 0; pitch < 12; pitch++) {
    const interval = mod12(pitch - chord.rootPitch)
    if (chordPitches.has(pitch)) {
      fits.set(pitch, 'chord')
      continue
    }
    const isDominantException = chord.isDominant && (interval === 1 || interval === 8)
    if (chordPitches.has(mod12(pitch - 1)) && !isDominantException) {
      fits.set(pitch, 'avoid')
      continue
    }
    const isTension =
      noteSet.has(pitch) || (chord.isDominant && [1, 3, 6, 8].includes(interval)) || (isMajorChord && interval === 6)
    if (isTension) {
      fits.set(pitch, 'tension')
    }
  }
  return fits
}

/**
 * A note's name where no role names it (the theory cues off, or neither key nor chords): the same speller, so
 * the roll never names one pitch two ways
 */
export const spellMelodyNote = (melody: Melody, index: number, key: Scale | undefined): NoteName =>
  keyContext(key).spell(melody.notes, index)
